import { Box, Button, ButtonGroup, Flex, HStack, IconButton, Input, SkeletonText, Text, Select } from '@chakra-ui/react';
import { FaLocationArrow, FaTimes, FaPlus, FaBars, FaMapMarkerAlt } from 'react-icons/fa';
import CompanyLogo from './Assets/magna.png';
import { useJsApiLoader, GoogleMap, Marker, Autocomplete, DirectionsRenderer, TrafficLayer } from '@react-google-maps/api';
import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function DirectionAPI() {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  const navigate = useNavigate();
  const [map, setMap] = useState(null);
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [alternativeRoutes, setAlternativeRoutes] = useState([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [distance, setDistance] = useState('');
  const [duration, setDuration] = useState('');
  const [waypoints, setWaypoints] = useState([]);
  const [additionalWaypoint, setAdditionalWaypoint] = useState('');
  const [rightPaneOpen, setRightPaneOpen] = useState(true);
  const [trafficLayer, setTrafficLayer] = useState(null);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [heading, setHeading] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [routePath, setRoutePath] = useState([]);
  const [intervalId, setIntervalId] = useState(null);
  const originRef = useRef();
  const destinationRef = useRef();

  useEffect(() => {
    if (map) {
      const trafficLayerInstance = new window.google.maps.TrafficLayer();
      trafficLayerInstance.setMap(map);
      setTrafficLayer(trafficLayerInstance);
    }
  }, [map]);

  useEffect(() => {
    if (navigator.geolocation) {
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude, heading } = position.coords;
          setCurrentPosition({ lat: latitude, lng: longitude });
          setHeading(heading);
          if (map) {
            map.panTo({ lat: latitude, lng: longitude });
          }
        },
        (error) => console.log(error),
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
      return () => navigator.geolocation.clearWatch(watchId);
    }
  }, [map]);

  useEffect(() => {
    if (isNavigating && map) {
      if (intervalId) {
        clearInterval(intervalId);
      }
      
      const newIntervalId = setInterval(() => {
        if (currentPosition) {
          const { lat, lng } = currentPosition;
          const markerLatLng = new window.google.maps.LatLng(lat, lng);
          
          // Check if the marker is close to the route path
          const path = routePath.map(point => new window.google.maps.LatLng(point.lat, point.lng));
          const distanceToPath = path.reduce((minDist, pathPoint) => {
            const distance = window.google.maps.geometry.spherical.computeDistanceBetween(markerLatLng, pathPoint);
            return Math.min(minDist, distance);
          }, Infinity);

          if (distanceToPath > 50) { // If the marker is far from the path, recalculate the route
            calculateRoute();
          }

          map.panTo({ lat, lng });
          map.setZoom(15);
        }
      }, 3000);

      setIntervalId(newIntervalId);
      return () => clearInterval(newIntervalId);
    }
  }, [isNavigating, currentPosition, routePath, map]);

  if (!isLoaded) {
    return <SkeletonText />;
  }

  async function calculateRoute() {
    if (!originRef.current.value && !currentPosition) {
      return;
    }
    if (!destinationRef.current.value) {
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();
    const waypointsLocations = waypoints.map(waypoint => ({ location: waypoint }));

    const origin = originRef.current.value || currentPosition;

    directionsService.route(
      {
        origin: origin,
        destination: destinationRef.current.value,
        waypoints: waypointsLocations,
        optimizeWaypoints: true,
        travelMode: window.google.maps.TravelMode.DRIVING,
        avoidTolls: true,
        provideRouteAlternatives: true,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirectionsResponse(result);
          setAlternativeRoutes(result.routes);
          setSelectedRouteIndex(0); // Default to the first route (usually the fastest)
          updateDistanceAndDuration(result.routes[0]);
          setRoutePath(result.routes[0].overview_path.map(point => ({
            lat: point.lat(),
            lng: point.lng(),
          })));
        } else if (status === 'REQUEST_DENIED') {
          alert('API Key tidak diizinkan untuk menggunakan layanan ini. Silakan hubungi administrator.');
          console.error('Directions request failed due to ' + status);
          // Optional: redirect to unauthorized page
          // window.location.href = '/unauthorized';
        } else {
          alert('Gagal mendapatkan rute: ' + status);
          console.error('Directions request failed due to ' + status);
        }
      }
    );
  }

  function updateDistanceAndDuration(route) {
    let totalDistance = 0;
    let totalDuration = 0;

    route.legs.forEach(leg => {
      totalDistance += leg.distance.value;
      totalDuration += leg.duration.value;
    });

    const totalDistanceInKm = (totalDistance / 1000).toFixed(2);
    const hours = Math.floor(totalDuration / 3600);
    const minutes = Math.floor((totalDuration % 3600) / 60);
    const totalDurationFormatted = `${hours} hours ${minutes} minutes`;

    setDistance(totalDistanceInKm + ' km');
    setDuration(totalDurationFormatted);
  }

  function clearRoute() {
    setDirectionsResponse(null);
    setAlternativeRoutes([]);
    setSelectedRouteIndex(0);
    setDistance('');
    setDuration('');
    originRef.current.value = '';
    destinationRef.current.value = '';
    setWaypoints([]);
    setRoutePath([]);
    setIsNavigating(false);
    if (intervalId) {
      clearInterval(intervalId);
    }
  }

  function addWaypoint() {
    if (additionalWaypoint.trim() !== '') {
      setWaypoints([...waypoints, additionalWaypoint.trim()]);
      setAdditionalWaypoint('');
    }
  }

  function removeWaypoint(index) {
    setWaypoints(waypoints.filter((_, i) => i !== index));
  }

  function getCurrentLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;
      
        const latLng = new window.google.maps.LatLng(latitude, longitude);

        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({ location: latLng }, (results, status) => {
          if (status === 'OK') {
            if (results[0]) {
              const formattedAddress = results[0].formatted_address;
              originRef.current.value = formattedAddress;
            } else {
              console.log('No results found');
            }
          } else {
            console.log('Geocoder failed due to: ' + status);
          }
        });
      });
    }
  }

  return (
    <Flex flexDirection='row' h='100vh' w='100vw' overflowX='hidden'>
      <Flex flexDirection='column' alignItems='center' w={rightPaneOpen ? 'calc(100vw - 350px)' : '100vw'} overflowX='hidden'>
        <Flex alignItems='center' justifyContent='space-between' w='100%' p={4} maxWidth='100%'>
          <Box>
            <img src={CompanyLogo} alt="Company Logo" style={{ width: '80px', height: '50px' }} onClick={() => navigate('/main-menu')}/>
          </Box>
          <IconButton aria-label='Toggle Right Pane' icon={<FaBars />} onClick={() => setRightPaneOpen(!rightPaneOpen)} />
        </Flex>

        <Box position='relative' flex='1' width='100%' maxWidth='100%' overflow='hidden'>
          <GoogleMap
            center={currentPosition || { lat: -6.229354611819955, lng: 106.81945787252144 }}
            zoom={15}
            mapContainerStyle={{ width: '100%', height: '100%' }}
            options={{
              zoomControl: true,
              streetViewControl: true,
              mapTypeControl: true,
              fullscreenControl: true,
            }}
            onLoad={map => setMap(map)}
          >
            {currentPosition && (
              <Marker position={currentPosition} icon={{
                path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 5,
                rotation: heading,
                fillColor: '#0000ff',
                fillOpacity: 1,
                strokeWeight: 2,
                strokeColor: '#ffffff',
              }} />
            )}
            {directionsResponse && alternativeRoutes.length > 0 && (
              <DirectionsRenderer
                directions={directionsResponse}
                routeIndex={selectedRouteIndex}
              />
            )}
            {waypoints.map((waypoint, index) => (
              <Marker key={index} position={waypoint} />
            ))}
            {trafficLayer && <TrafficLayer onLoad={trafficLayer => setTrafficLayer(trafficLayer)} />}
          </GoogleMap>
        </Box>
      </Flex>

      {rightPaneOpen && (
        <Box p={4} borderRadius='lg' m={4} bgColor='white' shadow='base' maxWidth='300px'>
          <Flex flexDirection='column' mt={4}>
            <Flex flexDirection='row'>
              <Box flex='1' mr={2}>
                <Autocomplete>
                  <Input type='text' placeholder='Origin' ref={originRef} />
                </Autocomplete>
              </Box>
              <Box>
                <IconButton
                  aria-label='Use Current Location'
                  icon={<FaMapMarkerAlt />}
                  onClick={getCurrentLocation}
                />
              </Box>
            </Flex>
            <Box flex='1' mt={4}>
              <Autocomplete>
                <Input type='text' placeholder='Destination' ref={destinationRef} />
              </Autocomplete>
            </Box>
            <Flex flexDirection='column' mt={4} mb={4}>
              {waypoints.map((waypoint, index) => (
                <Flex key={index} alignItems='center' mb={2}>
                  <Text>{waypoint}</Text>
                  <IconButton aria-label='Remove Waypoint' icon={<FaTimes />} ml={2} onClick={() => removeWaypoint(index)} />
                </Flex>
              ))}
              <Flex flexDirection='row'>
                <Box flex='1'>
                  <Autocomplete>
                    <Input
                      type='text'
                      placeholder='Add Waypoint'
                      value={additionalWaypoint}
                      onChange={e => setAdditionalWaypoint(e.target.value)}
                    />
                  </Autocomplete>
                </Box>
                <IconButton aria-label='Add Waypoint' icon={<FaPlus />} ml={2} onClick={addWaypoint} />
              </Flex>
            </Flex>
            <Box>
              <ButtonGroup>
                <Button colorScheme='blue' type='submit' onClick={calculateRoute}>Calculate Route</Button>
                <IconButton aria-label='Clear Route' icon={<FaTimes />} onClick={clearRoute} />
              </ButtonGroup>
            </Box>
            <Button colorScheme='blue' mt={4} onClick={() => setIsNavigating(true)}>Start Navigation</Button>
            <HStack spacing={4} mt={4} justifyContent='space-between'>
              <Text>Distance: {distance}</Text>
              <Text>Duration: {duration}</Text>
              <IconButton
                aria-label='Center Back'
                icon={<FaLocationArrow />}
                isRound
                onClick={() => {
                  map.panTo(currentPosition);
                  map.setZoom(15);
                }}
              />
            </HStack>
            {alternativeRoutes.length > 1 && (
              <Box mt={4}>
                <Text fontWeight='bold'>Select Route</Text>
                <Select
                  placeholder='Select route'
                  value={selectedRouteIndex}
                  onChange={(e) => {
                    const index = parseInt(e.target.value);
                    setSelectedRouteIndex(index);
                    updateDistanceAndDuration(alternativeRoutes[index]);
                    setRoutePath(alternativeRoutes[index].overview_path.map(point => ({
                      lat: point.lat(),
                      lng: point.lng(),
                    })));
                  }}
                  borderRadius='md'
                  borderColor='gray.300'
                  mt={2}
                >
                  {alternativeRoutes.map((_, index) => (
                    <option key={index} value={index}>
                      Route {index + 1}
                    </option>
                  ))}
                </Select>
              </Box>
            )}
          </Flex>
        </Box>
      )}
    </Flex>
  );
}

export default DirectionAPI;
